"use client";

import { c as _c } from "react/compiler-runtime";
import type { AvailabilityFilter, OptionFilter, PriceRangeFilter, ProductFiltersResponse } from "@spree/sdk";
import { SlidersHorizontal } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import type { JSX } from "react";
import { memo, useCallback, useMemo, useState } from "react";
import { AvailabilityDropdownContent } from "@/components/products/filters/AvailabilityDropdownContent";
import { FilterBarSkeleton } from "@/components/products/filters/FilterBarSkeleton";
import { FilterChips } from "@/components/products/filters/FilterChips";
import { FilterDropdown } from "@/components/products/filters/FilterDropdown";
import { MobileFilterDrawer } from "@/components/products/filters/MobileFilterDrawer";
import { OptionDropdownContent } from "@/components/products/filters/OptionDropdownContent";
import { PriceDropdownContent } from "@/components/products/filters/PriceDropdownContent";
import { SortDropdownContent } from "@/components/products/filters/SortDropdownContent";
import { getActiveFilterCount } from "@/lib/utils/filters";
import { generatePriceBuckets } from "@/lib/utils/price-buckets";
import type { ActiveFilters, AvailabilityStatus } from "@/types/filters";
interface FilterBarProps {
  filtersData: ProductFiltersResponse | null;
  filtersLoading: boolean;
  activeFilters: ActiveFilters;
  totalCount: number;
  onFilterChange: (filters: ActiveFilters) => void;
}
export const FilterBar = memo(function FilterBar(t0) {
  const $ = _c(135);
  const {
    filtersData,
    filtersLoading,
    activeFilters,
    totalCount,
    onFilterChange
  } = t0;
  const t = useTranslations("products");
  const locale = useLocale();
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = id => {
      setOpenDropdownId(prev => prev === id ? null : id);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const toggleDropdown = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setOpenDropdownId(null);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const closeDropdown = t2;
  let t3;
  if ($[2] !== activeFilters || $[3] !== onFilterChange) {
    t3 = optionValueId => {
      const newOptionValues = activeFilters.optionValues.includes(optionValueId) ? activeFilters.optionValues.filter(id_0 => id_0 !== optionValueId) : [...activeFilters.optionValues, optionValueId];
      onFilterChange({
        ...activeFilters,
        optionValues: newOptionValues
      });
    };
    $[2] = activeFilters;
    $[3] = onFilterChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleOptionValueToggle = t3;
  let t4;
  if ($[5] !== activeFilters || $[6] !== onFilterChange) {
    t4 = (min, max) => {
      onFilterChange({
        ...activeFilters,
        priceMin: min,
        priceMax: max
      });
    };
    $[5] = activeFilters;
    $[6] = onFilterChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handlePriceChange = t4;
  let t5;
  if ($[8] !== activeFilters || $[9] !== onFilterChange) {
    t5 = availability => {
      onFilterChange({
        ...activeFilters,
        availability
      });
    };
    $[8] = activeFilters;
    $[9] = onFilterChange;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleAvailabilityChange = t5;
  let t6;
  if ($[11] !== activeFilters || $[12] !== onFilterChange) {
    t6 = sortBy => {
      onFilterChange({
        ...activeFilters,
        sortBy
      });
      closeDropdown();
    };
    $[11] = activeFilters;
    $[12] = onFilterChange;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleSortChange = t6;
  let t7;
  if ($[14] !== activeFilters.sortBy || $[15] !== onFilterChange) {
    t7 = () => {
      onFilterChange({
        optionValues: [],
        priceMin: undefined,
        priceMax: undefined,
        availability: undefined,
        sortBy: activeFilters.sortBy
      });
    };
    $[14] = activeFilters.sortBy;
    $[15] = onFilterChange;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const clearFilters = t7;
  let t8;
  if ($[17] !== filtersData || $[18] !== locale || $[19] !== t) {
    bb0: {
      if (!filtersData) {
        let t9;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = [];
          $[21] = t9;
        } else {
          t9 = $[21];
        }
        t8 = t9;
        break bb0;
      }
      const priceFilter = filtersData.filters.find(_temp) as PriceRangeFilter | undefined;
      if (!priceFilter) {
        let t9;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = [];
          $[22] = t9;
        } else {
          t9 = $[22];
        }
        t8 = t9;
        break bb0;
      }
      t8 = generatePriceBuckets(Number(priceFilter.min), Number(priceFilter.max), priceFilter.currency, {
        t,
        locale
      });
    }
    $[17] = filtersData;
    $[18] = locale;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const priceBuckets = t8;
  let t9;
  bb1: {
    if (!filtersData) {
      let t10;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = [];
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      t9 = t10;
      break bb1;
    }
    let t10;
    if ($[24] !== filtersData.filters) {
      t10 = filtersData.filters.filter(_temp2);
      $[24] = filtersData.filters;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    t9 = t10 as OptionFilter[];
  }
  const optionFilters = t9;
  let counts;
  if ($[26] !== activeFilters.optionValues || $[27] !== optionFilters) {
    counts = {};
    for (const filter of optionFilters) {
      counts[filter.id] = filter.options.filter(o => activeFilters.optionValues.includes(o.id)).length;
    }
    $[26] = activeFilters.optionValues;
    $[27] = optionFilters;
    $[28] = counts;
  } else {
    counts = $[28];
  }
  const badgeCounts = counts;
  const priceBadge = activeFilters.priceMin !== undefined || activeFilters.priceMax !== undefined ? 1 : 0;
  const availabilityBadge = activeFilters.availability ? 1 : 0;
  const totalActiveFilters = getActiveFilterCount(activeFilters);
  const hasActiveFilters = totalActiveFilters > 0;
  const activeSortBy = activeFilters.sortBy || filtersData?.default_sort;
  if (!filtersData) {
    if (filtersLoading) {
      let t10;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <FilterBarSkeleton />;
        $[29] = t10;
      } else {
        t10 = $[29];
      }
      return t10;
    }
    return null;
  }
  let t10;
  if ($[30] !== filtersData.filters) {
    t10 = filtersData.filters.find(_temp3);
    $[30] = filtersData.filters;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const availabilityFilter = t10 as AvailabilityFilter | undefined;
  let t11;
  if ($[32] !== filtersData.filters || $[33] !== priceBuckets) {
    t11 = filtersData.filters.some(_temp4) && priceBuckets.length > 0;
    $[32] = filtersData.filters;
    $[33] = priceBuckets;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const hasPriceFilter = t11;
  let t12;
  if ($[35] !== activeFilters.optionValues || $[36] !== badgeCounts || $[37] !== handleOptionValueToggle || $[38] !== openDropdownId || $[39] !== optionFilters) {
    let t13;
    if ($[41] !== activeFilters.optionValues || $[42] !== badgeCounts || $[43] !== handleOptionValueToggle || $[44] !== openDropdownId) {
      t13 = filter_0 => <FilterDropdown key={filter_0.id} label={filter_0.label} badgeCount={badgeCounts[filter_0.id]} isOpen={openDropdownId === filter_0.id} onToggle={() => toggleDropdown(filter_0.id)} onClose={closeDropdown}><OptionDropdownContent filter={filter_0} selectedValues={activeFilters.optionValues} onToggle={handleOptionValueToggle} /></FilterDropdown>;
      $[41] = activeFilters.optionValues;
      $[42] = badgeCounts;
      $[43] = handleOptionValueToggle;
      $[44] = openDropdownId;
      $[45] = t13;
    } else {
      t13 = $[45];
    }
    t12 = optionFilters.map(t13);
    $[35] = activeFilters.optionValues;
    $[36] = badgeCounts;
    $[37] = handleOptionValueToggle;
    $[38] = openDropdownId;
    $[39] = optionFilters;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[46] !== activeFilters || $[47] !== handlePriceChange || $[48] !== hasPriceFilter || $[49] !== openDropdownId || $[50] !== priceBadge || $[51] !== priceBuckets || $[52] !== t) {
    t13 = hasPriceFilter && <FilterDropdown label={t("price")} badgeCount={priceBadge} isOpen={openDropdownId === "price"} onToggle={() => toggleDropdown("price")} onClose={closeDropdown}><PriceDropdownContent priceBuckets={priceBuckets} activeFilters={activeFilters} onPriceChange={handlePriceChange} /></FilterDropdown>;
    $[46] = activeFilters;
    $[47] = handlePriceChange;
    $[48] = hasPriceFilter;
    $[49] = openDropdownId;
    $[50] = priceBadge;
    $[51] = priceBuckets;
    $[52] = t;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== activeFilters.availability || $[55] !== availabilityBadge || $[56] !== availabilityFilter || $[57] !== handleAvailabilityChange || $[58] !== openDropdownId || $[59] !== t) {
    t14 = availabilityFilter && <FilterDropdown label={t("availability")} badgeCount={availabilityBadge} isOpen={openDropdownId === "availability"} onToggle={() => toggleDropdown("availability")} onClose={closeDropdown}><AvailabilityDropdownContent filter={availabilityFilter} selected={activeFilters.availability} onChange={handleAvailabilityChange} /></FilterDropdown>;
    $[54] = activeFilters.availability;
    $[55] = availabilityBadge;
    $[56] = availabilityFilter;
    $[57] = handleAvailabilityChange;
    $[58] = openDropdownId;
    $[59] = t;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  let t15;
  if ($[61] !== t12 || $[62] !== t13 || $[63] !== t14) {
    t15 = <div className="flex items-center gap-3">{t12}{t13}{t14}</div>;
    $[61] = t12;
    $[62] = t13;
    $[63] = t14;
    $[64] = t15;
  } else {
    t15 = $[64];
  }
  let t16;
  if ($[65] !== t || $[66] !== totalCount) {
    t16 = t("productCount", {
      count: totalCount
    });
    $[65] = t;
    $[66] = totalCount;
    $[67] = t16;
  } else {
    t16 = $[67];
  }
  let t17;
  if ($[68] !== t16) {
    t17 = <span className="text-sm text-gray-500">{t16}</span>;
    $[68] = t16;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  let t18;
  if ($[70] !== t) {
    t18 = t("sort");
    $[70] = t;
    $[71] = t18;
  } else {
    t18 = $[71];
  }
  const t19 = openDropdownId === "sort";
  let t20;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => toggleDropdown("sort");
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  let t21;
  if ($[73] !== activeSortBy || $[74] !== filtersData.sort_options || $[75] !== handleSortChange) {
    t21 = <SortDropdownContent sortOptions={filtersData.sort_options} activeSortBy={activeSortBy} onSortChange={handleSortChange} />;
    $[73] = activeSortBy;
    $[74] = filtersData.sort_options;
    $[75] = handleSortChange;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  let t22;
  if ($[77] !== t18 || $[78] !== t19 || $[79] !== t21) {
    t22 = <FilterDropdown label={t18} isOpen={t19} onToggle={t20} onClose={closeDropdown} align="right">{t21}</FilterDropdown>;
    $[77] = t18;
    $[78] = t19;
    $[79] = t21;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] !== t17 || $[82] !== t22) {
    t23 = <div className="flex items-center gap-3">{t17}{t22}</div>;
    $[81] = t17;
    $[82] = t22;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  let t24;
  if ($[84] !== t15 || $[85] !== t23) {
    t24 = <div className="hidden md:flex items-center justify-between pb-4 border-b border-gray-100">{t15}{t23}</div>;
    $[84] = t15;
    $[85] = t23;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setShowMobileDrawer(true);
    $[87] = t25;
  } else {
    t25 = $[87];
  }
  const t26 = `flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${hasActiveFilters ? "border-gray-500 bg-gray-50 text-primary" : "border-gray-300 text-gray-700"}`;
  let t27;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <SlidersHorizontal className="w-4 h-4" />;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  if ($[89] !== t) {
    t28 = t("filters");
    $[89] = t;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== t28) {
    t29 = <span>{t28}</span>;
    $[91] = t28;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  let t30;
  if ($[93] !== hasActiveFilters || $[94] !== totalActiveFilters) {
    t30 = hasActiveFilters && <span className="flex items-center justify-center w-5 h-5 text-xs bg-primary text-white rounded-lg">{totalActiveFilters}</span>;
    $[93] = hasActiveFilters;
    $[94] = totalActiveFilters;
    $[95] = t30;
  } else {
    t30 = $[95];
  }
  let t31;
  if ($[96] !== t26 || $[97] !== t29 || $[98] !== t30) {
    t31 = <button type="button" onClick={t25} className={t26}>{t27}{t29}{t30}</button>;
    $[96] = t26;
    $[97] = t29;
    $[98] = t30;
    $[99] = t31;
  } else {
    t31 = $[99];
  }
  let t32;
  if ($[100] !== t) {
    t32 = t("sort");
    $[100] = t;
    $[101] = t32;
  } else {
    t32 = $[101];
  }
  const t33 = openDropdownId === "sort-mobile";
  let t34;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => toggleDropdown("sort-mobile");
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== activeSortBy || $[104] !== filtersData.sort_options || $[105] !== handleSortChange) {
    t35 = <SortDropdownContent sortOptions={filtersData.sort_options} activeSortBy={activeSortBy} onSortChange={handleSortChange} />;
    $[103] = activeSortBy;
    $[104] = filtersData.sort_options;
    $[105] = handleSortChange;
    $[106] = t35;
  } else {
    t35 = $[106];
  }
  let t36;
  if ($[107] !== t32 || $[108] !== t33 || $[109] !== t35) {
    t36 = <div className="ml-auto"><FilterDropdown label={t32} isOpen={t33} onToggle={t34} onClose={closeDropdown} align="right">{t35}</FilterDropdown></div>;
    $[107] = t32;
    $[108] = t33;
    $[109] = t35;
    $[110] = t36;
  } else {
    t36 = $[110];
  }
  let t37;
  if ($[111] !== t31 || $[112] !== t36) {
    t37 = <div className="flex items-center gap-3 md:hidden pb-4 border-b border-gray-100">{t31}{t36}</div>;
    $[111] = t31;
    $[112] = t36;
    $[113] = t37;
  } else {
    t37 = $[113];
  }
  let t38;
  if ($[114] !== activeFilters || $[115] !== clearFilters || $[116] !== filtersData || $[117] !== handleAvailabilityChange || $[118] !== handleOptionValueToggle || $[119] !== handlePriceChange || $[120] !== hasActiveFilters || $[121] !== priceBuckets) {
    t38 = hasActiveFilters && <FilterChips activeFilters={activeFilters} filtersData={filtersData} priceBuckets={priceBuckets} onRemoveOptionValue={id_1 => handleOptionValueToggle(id_1)} onRemovePrice={() => handlePriceChange(undefined, undefined)} onRemoveAvailability={() => handleAvailabilityChange(undefined)} onClearAll={clearFilters} />;
    $[114] = activeFilters;
    $[115] = clearFilters;
    $[116] = filtersData;
    $[117] = handleAvailabilityChange;
    $[118] = handleOptionValueToggle;
    $[119] = handlePriceChange;
    $[120] = hasActiveFilters;
    $[121] = priceBuckets;
    $[122] = t38;
  } else {
    t38 = $[122];
  }
  let t39;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = () => setShowMobileDrawer(false);
    $[123] = t39;
  } else {
    t39 = $[123];
  }
  let t40;
  if ($[124] !== activeFilters || $[125] !== filtersData || $[126] !== onFilterChange || $[127] !== priceBuckets || $[128] !== showMobileDrawer) {
    t40 = <MobileFilterDrawer isOpen={showMobileDrawer} onClose={t39} filtersData={filtersData} activeFilters={activeFilters} priceBuckets={priceBuckets} onApply={onFilterChange} />;
    $[124] = activeFilters;
    $[125] = filtersData;
    $[126] = onFilterChange;
    $[127] = priceBuckets;
    $[128] = showMobileDrawer;
    $[129] = t40;
  } else {
    t40 = $[129];
  }
  let t41;
  if ($[130] !== t24 || $[131] !== t37 || $[132] !== t38 || $[133] !== t40) {
    t41 = <div className="mb-6">{t24}{t37}{t38}{t40}</div>;
    $[130] = t24;
    $[131] = t37;
    $[132] = t38;
    $[133] = t40;
    $[134] = t41;
  } else {
    t41 = $[134];
  }
  return t41;
});
function _temp(f) {
  return f.type === "price_range";
}
function _temp2(f_0) {
  return f_0.type === "option";
}
function _temp3(f_1) {
  return f_1.type === "availability";
}
function _temp4(f_2) {
  return f_2.type === "price_range";
}